import { AddAlert } from "@mui/icons-material";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import EmailIcon from "@mui/icons-material/Email";
import { Box, Button, Typography } from '@mui/material';
import React, { useState } from 'react';
import { Link } from "react-router-dom";
import ApiClient from '../config/ApiClient';
import { AthletePaymentAssignment, RelEntity } from '../object-actions/types/types';

interface PreAssessmentCtaProps {
    assignment: AthletePaymentAssignment;
}

const PreAssessmentCta: React.FC<PreAssessmentCtaProps> = ({ assignment }) => {
    // State for reminder email sending
    const [sendingReminder, setSendingReminder] = useState(false);
    const [reminderSent, setReminderSent] = useState(false);
    const [reminderError, setReminderError] = useState<string | null>(null);

    // Handler for sending assessment reminder email
    const handleSendReminder = async () => {
        if (!assignment.paymentassignment_ids || assignment.paymentassignment_ids.length === 0) {
            setReminderError('Assignment ID not found');
            return;
        }

        setSendingReminder(true);
        setReminderError(null);

        try {
            const assignmentId = assignment.paymentassignment_ids[0];
            const response = await ApiClient.post(`/api/payment-assignments/${assignmentId}/send-assessment-reminder/`, {});

            if (response.success) {
                setReminderSent(true);
            } else {
                setReminderError(response.error || 'Failed to send reminder email');
            }
        } catch (error) {
            setReminderError(error instanceof Error ? error.message : 'An error occurred while sending the reminder');
        } finally {
            setSendingReminder(false);
        }
    };

    if (assignment.pre_assessment_submitted_at) {
        return <Box display="flex" alignItems="center" gap={0.5}>
            <CheckCircleOutline color="secondary" sx={{ fontSize: 16 }} />
            <Typography component="span">Pre-Assessment Submitted</Typography>
        </Box>
    }

    const isCoach = assignment.my_roles.includes('coach');

    const firstUnsubmitted = assignment.submissions.find(sub => sub.entity && !sub.entity.submitted_at) as RelEntity<'AssessmentSubmission'>;
    if (firstUnsubmitted && !isCoach) {
        return <Box display="flex" alignItems="center" gap={0.5}>
            <Button
                sx={{ mt: 1 }}
                variant="contained"
                color="secondary"
                size="large"
                component={Link}
                to={`/assessments/${assignment.pre_assessment?.id}`}
            >
                Resume Assessment
            </Button>
        </Box>
    }

    if (firstUnsubmitted && isCoach) {
        return <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body2" color="text.secondary">
                {firstUnsubmitted.entity?.response_count} responses submitted
            </Typography>
            {reminderSent ? (
                <Typography variant="body2" color="success.main">
                    Reminder email sent successfully!
                </Typography>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EmailIcon />}
                    onClick={handleSendReminder}
                    disabled={sendingReminder}
                    sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                >
                    {sendingReminder ? 'Sending...' : 'Send Reminder Email'}
                </Button>
            )}
            {reminderError && (
                <Typography variant="body2" color="error">
                    {reminderError}
                </Typography>
            )}
        </Box>
    }

    if (!isCoach) return <Button
        sx={{ mt: 1 }}
        variant="contained"
        color="secondary"
        size="large"
        component={Link}
        to={`/assessments/${assignment.pre_assessment?.id}`}
    >
        Take Assessment
    </Button>

    return <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
            <AddAlert color="error" sx={{ fontSize: 15 }} />
            Assessment not yet started
        </Typography>
        {reminderSent ? (
            <Typography variant="body2" color="success.main">
                Reminder email sent successfully!
            </Typography>
        ) : (
            <Button
                variant="outlined"
                size="small"
                startIcon={<EmailIcon />}
                onClick={handleSendReminder}
                disabled={sendingReminder}
                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
            >
                {sendingReminder ? 'Sending...' : 'Send Reminder Email'}
            </Button>
        )}
        {reminderError && (
            <Typography variant="body2" color="error">
                {reminderError}
            </Typography>
        )}
    </Box>

};

export default PreAssessmentCta;
